import { useEffect, useRef } from 'react';
import { onRealtimeReconnect } from '@/lib/realtime';
import { RESYNC_POLL_MS, startResync } from '@/lib/resync';

/**
 * Re-read the server whenever this screen may have missed something.
 *
 * Every guest surface that shows live state calls this beside its `useRealtime`
 * subscriptions, and it is the *only* thing any of them do about a missed push
 * — see `@/lib/resync` for the three sensors and why one alone is not enough.
 *
 * It deliberately does not fire on mount: the screen's own load already ran,
 * and a catch-up there would be a second identical request on every navigation.
 *
 * `pollMs` is for a surface whose truth is a poll rather than a push — the
 * resource meters, which the server does not emit to a guest room at all — and
 * is otherwise left alone.
 */
export function useResync(onResync: () => void, pollMs: number = RESYNC_POLL_MS) {
  const handlerRef = useRef(onResync);
  handlerRef.current = onResync;

  useEffect(
    () =>
      startResync({
        onResync: () => handlerRef.current(),
        onReconnect: onRealtimeReconnect,
        pollMs,
      }),
    [pollMs],
  );
}
